import { AuthContext } from '@enter/core';
import * as d3 from 'd3';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { IObservedRootStore } from 'stores';
import * as S from './inactivity-dialog.styles';

interface IInactivityDialogProps extends IObservedRootStore {
    onClose: () => void;
}

const logoutCountdown: number = 60; // seconds

export const InactivityDialog: React.FC<IInactivityDialogProps> = inject('rootStore')(
    observer(
        (props: IInactivityDialogProps): React.ReactElement => {
            const { rootStore, onClose } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const [countdown, setCountdown] = useState<number>(logoutCountdown);
            const [isExiting, setExiting] = useState(false);

            const formatDuration = (duration: number): string => {
                return d3.timeFormat('%M:%S')(new Date(duration * 1000));
            };

            // terminate the session by logging out
            const forceLogout = (): void => {
                setExiting(true);
                authContext.logoutAndRedirect && authContext.logoutAndRedirect();
            };

            // event handlers
            const handleLogoutClick = (): void => {
                forceLogout();
            };
            const handleContinueClick = (): void => {
                rootStore.loadUser();
                onClose();
            };

            useEffect(() => {
                const t = setInterval(() => {
                    setCountdown(countdown - 1);
                }, 1000);
                return () => {
                    clearInterval(t);
                };
            }, [countdown]);

            return (
                <S.DialogBody>
                    <S.DialogMessage>
                        {!isExiting && (
                            <>
                                You appear to be inactive. For your protection your session will be ended automatically.
                            </>
                        )}
                        {!isExiting && <S.Countdown>Logging off in {formatDuration(countdown)}</S.Countdown>}
                        {isExiting && <>You are being logged out.</>}
                    </S.DialogMessage>
                    <S.DialogActions>
                        <S.DialogButton variant="outlined" disabled={isExiting} onClick={handleLogoutClick}>
                            Logout now
                        </S.DialogButton>
                        <S.DialogButton variant="primary" disabled={isExiting} onClick={handleContinueClick}>
                            Continue
                        </S.DialogButton>
                    </S.DialogActions>
                </S.DialogBody>
            );
        },
    ),
);
